import escapeStringRegexp from 'escape-string-regexp'
import { FunctionComponent, useMemo } from 'react'
import { matchPath, useLocation, To } from 'react-router'
import styled from 'styled-components'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { toCatalogDetail, toDataDetail, toGeoSearch } from '../../../../links'
import {
  centerParam,
  legendOpenParam,
  locationParam,
  mapLayersParam,
  ViewMode,
  viewParam,
  zoomParam,
} from '../../../../pages/MapPage/query-params'
import { queryParam } from '../../../../pages/SearchPage/query-params'
import toSearchParams from '../../../utils/toSearchParams'
import type { AutoSuggestSearchContent } from './AutoSuggest'
import useParam from '../../../hooks/useParam'
import { routing } from '../../../../routes'
import { useHeaderSearch } from '../../../contexts/HeaderSearch/HeaderSearchContext'
import { getDetailPageData } from '../../../utils/searchUtils'
import { isAuthenticated } from '../../../utils/auth/auth'
import { DataType } from '../../../../pages/SearchPage/types'
import Link from '../../Links/Link/Link'

export interface AutoSuggestItemProps {
  content: string
  suggestion: AutoSuggestSearchContent
  highlightValue: string
  label: string
  toggleMenu?: () => void
}

export const StyledLink = styled(Link)`
  font-size: var(--body-font-size);
  line-height: var(--body-line-height);
  display: block;
  font-weight: 700;
  margin-left: 0;
  position: relative;
  text-align: left;
  width: 100%;
  text-decoration: none;

  &:hover,
  &:active,
  &.auto-suggest__dropdown-item--active {
    color: var(--blue);
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 2px;
  }

  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const AutoSuggestItem: FunctionComponent<AutoSuggestItemProps> = ({
  content,
  suggestion,
  highlightValue,
  toggleMenu,
}) => {
  const appInsights = useAppInsightsContext()
  const trackSearchEvent = useTrackEvent(appInsights, 'SELECTED_SEARCH_CATEGORY', {})

  const [view] = useParam(viewParam)
  const [locationParameter] = useParam(locationParam)
  const [zoom] = useParam(zoomParam)
  const [center] = useParam(centerParam)
  const location = useLocation()
  const { setSearchInputValue } = useHeaderSearch()

  const to = useMemo<To>(() => {
    if (suggestion.type === DataType.Catalog) {
      return {
        ...toCatalogDetail(suggestion.id),
        search: toSearchParams([[queryParam, suggestion.label]]).toString(),
      }
    }
    if (suggestion.type === DataType.Map) {
      const { searchParams } = new URL(suggestion.uri, window.location.origin)
      const rawMapLayers = searchParams.get(mapLayersParam.name)
      const mapLayers = rawMapLayers ? mapLayersParam.decode(rawMapLayers) : []

      // Set detail-page path if current route is on a detail page or on dataSelectionLegacy page, otherwise navigate to geosearch page
      const pathname =
        matchPath({ path: routing.dataDetail.path, end: true }, location.pathname)?.pathname ??
        view === ViewMode.Map
          ? matchPath({ path: routing.addresses.path, end: true }, location.pathname)?.pathname ??
            matchPath({ path: routing.cadastralObjects.path, end: true }, location.pathname)
              ?.pathname ??
            matchPath({ path: routing.establishments.path, end: true }, location.pathname)?.pathname
          : toGeoSearch().pathname

      return {
        pathname: pathname ?? undefined,
        search: toSearchParams(
          [
            [viewParam, ViewMode.Map],
            [queryParam, suggestion.label],
            [mapLayersParam, mapLayers],
            [locationParam, locationParameter],
            [legendOpenParam, true],
          ],
          { initialValue: location.search },
        ).toString(),
      }
    }

    const currentSearchParams = new URLSearchParams(window.location.search)
    const rawMapLayers = currentSearchParams.get(mapLayersParam.name)
    const mapLayers = rawMapLayers ? mapLayersParam.decode(rawMapLayers) : []
    const { type, subtype, id } = getDetailPageData(suggestion)
    const path = toDataDetail({ type, subtype, id })

    return {
      pathname: path.pathname,
      search: toSearchParams([
        [viewParam, view],
        [queryParam, content],
        [locationParam, locationParameter],
        [mapLayersParam, mapLayers],
        [zoomParam, zoom],
        [centerParam, center],
      ]).toString(),
    }
  }, [highlightValue, location])

  const htmlContent = useMemo(
    () => highlightSuggestion(content, highlightValue),
    [content, highlightValue],
  )

  const handleLinkClick = () => {
    if (isAuthenticated()) {
      trackSearchEvent({ category: suggestion.category, theQuery: content })
    }
    setSearchInputValue(content)

    if (toggleMenu) {
      toggleMenu()
    }
  }

  return (
    <StyledLink data-testid="autoSuggestDropdownItem" onClick={handleLinkClick} to={to}>
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: htmlContent,
        }}
      />
    </StyledLink>
  )
}

function highlightSuggestion(content: string, highlightValue: string) {
  return content.replace(
    new RegExp(`(${escapeStringRegexp(highlightValue.trim())})`, 'gi'),
    '<span>$1</span>',
  )
}

export default AutoSuggestItem
